import React from 'react';
import {
  Briefcase,
  User,
  Calendar,
  Compass,
  Award,
  FileText,
  Clock,
  Zap,
  MoreVertical,
  ChevronRight,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { PracticeCase, CaseStage, PriorityLevel, ReportStatus } from '../../types/practiceManagement';
import { Language } from '../../types/kundali';

interface CaseCardProps {
  caseItem: PracticeCase;
  lang: Language;
  onOpenCaseWorkspace: (caseItem: PracticeCase) => void;
  onOpenProAnalysis?: (caseItem: PracticeCase) => void;
  onOpenAcharyaConsultation?: (caseItem: PracticeCase) => void;
  onPrepareReport?: (caseItem: PracticeCase) => void;
  onViewClient?: (clientId: string) => void;
  onMoveStage?: (caseId: string, newStage: CaseStage) => void;
  compact?: boolean;
}

const STAGE_LABELS: Record<CaseStage, { en: string; hi: string; color: string }> = {
  NEW: { en: 'New Case', hi: 'नया केस', color: 'bg-blue-500/15 text-blue-800 dark:text-blue-300 border-blue-500/30' },
  ANALYSIS: { en: 'In Analysis', hi: 'विश्लेषणरत', color: 'bg-astro-secondary-surface0/15 text-astro-secondary dark:text-amber-300 border-astro-accent/30' },
  REVIEW: { en: 'Review', hi: 'समीक्षा', color: 'bg-purple-500/15 text-purple-800 dark:text-purple-300 border-purple-500/30' },
  CONSULTATION: { en: 'Consultation', hi: 'परामर्श', color: 'bg-orange-500/15 text-orange-900 dark:text-orange-300 border-orange-500/30' },
  REPORT_PREP: { en: 'Report Prep', hi: 'रिपोर्ट तैयारी', color: 'bg-indigo-500/15 text-indigo-800 dark:text-indigo-300 border-indigo-500/30' },
  DELIVERED: { en: 'Delivered', hi: 'वितरित', color: 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 border-emerald-500/30' },
  FOLLOW_UP: { en: 'Follow-Up', hi: 'फॉलो-अप', color: 'bg-teal-500/15 text-teal-800 dark:text-teal-300 border-teal-500/30' }
};

const PRIORITY_BADGES: Record<PriorityLevel, { label: string; class: string }> = {
  HIGH: { label: 'High Priority', class: 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/20' },
  MEDIUM: { label: 'Standard', class: 'bg-astro-secondary-surface dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted border-astro-border dark:border-astro-strong-border' },
  LOW: { label: 'Routine', class: 'bg-astro-secondary-surface dark:bg-slate-850 text-astro-muted dark:text-astro-muted border-astro-border dark:border-astro-strong-border' }
};

export const CaseCard: React.FC<CaseCardProps> = ({
  caseItem,
  lang,
  onOpenCaseWorkspace,
  onOpenProAnalysis,
  onOpenAcharyaConsultation,
  onPrepareReport,
  onViewClient,
  onMoveStage,
  compact = false
}) => {
  const isHi = lang === 'hi';
  const stageInfo = STAGE_LABELS[caseItem.stage] || STAGE_LABELS.NEW;
  const priorityInfo = PRIORITY_BADGES[caseItem.priority] || PRIORITY_BADGES.MEDIUM;

  return (
    <div
      id={`case-card-${caseItem.id}`}
      className="group bg-white dark:bg-slate-850 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs hover:shadow-md transition-all duration-200 p-4 flex flex-col justify-between gap-3 relative"
    >
      {/* Top Header Row */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[11px] font-mono font-bold text-[#52635F] dark:text-amber-400 bg-astro-secondary-surface dark:bg-amber-950/40 px-2 py-0.5 rounded border border-[#E2DED5] dark:border-amber-800">
              {caseItem.caseNumber}
            </span>
            <span
              className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${stageInfo.color}`}
            >
              {isHi ? stageInfo.hi : stageInfo.en}
            </span>
            {caseItem.priority === 'HIGH' && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${priorityInfo.class}`}>
                {priorityInfo.label}
              </span>
            )}
          </div>

          <button
            onClick={() => onOpenCaseWorkspace(caseItem)}
            className="text-left font-bold text-astro-text dark:text-astro-muted hover:text-amber-600 dark:hover:text-amber-400 text-sm transition-colors line-clamp-1 cursor-pointer"
          >
            {caseItem.clientName}
          </button>
          <div className="text-[11px] font-semibold text-astro-secondary/90 dark:text-amber-300/90 flex items-center gap-1.5">
            <span>{isHi ? caseItem.themeTitleHi : caseItem.themeTitleEn}</span>
          </div>
        </div>

        {caseItem.timingAlertIndicator?.hasAlert && (
          <div
            title={`Timing Confluence: ${caseItem.timingAlertIndicator.title}`}
            className="p-1.5 rounded-lg bg-astro-secondary-surface0/15 border border-astro-accent/30 text-amber-600 dark:text-amber-400 shrink-0"
          >
            <Zap className="w-3.5 h-3.5" />
          </div>
        )}
      </div>

      {/* Target Question */}
      <div className="text-xs text-astro-secondary-text dark:text-astro-muted line-clamp-2 bg-astro-secondary-surface dark:bg-astro-primary/50 p-2.5 rounded-lg border border-astro-border dark:border-astro-strong-border italic">
        "{caseItem.targetQuestion}"
      </div>

      {/* Next Action & Timing Indicator */}
      <div className="space-y-1.5 text-[11px]">
        {caseItem.nextAction && (
          <div className="flex items-start gap-1.5 text-astro-secondary-text dark:text-astro-muted">
            <span className="font-semibold text-astro-muted dark:text-astro-muted shrink-0">
              {isHi ? 'अगली कार्रवाई:' : 'Next:'}
            </span>
            <span className="line-clamp-1">{caseItem.nextAction}</span>
          </div>
        )}

        <div className="flex items-center justify-between text-[11px] text-astro-secondary-text dark:text-astro-muted pt-1 border-t border-astro-border dark:border-astro-strong-border">
          <div className="flex items-center gap-1 truncate">
            <User className="w-3 h-3 text-astro-muted dark:text-astro-muted" />
            <span className="truncate">{caseItem.assignedPractitioner}</span>
          </div>
          <div className="flex items-center gap-1 shrink-0 text-astro-secondary-text dark:text-astro-muted">
            <Clock className="w-3 h-3 text-astro-muted dark:text-astro-muted" />
            <span>{caseItem.lastActivityDate}</span>
          </div>
        </div>
      </div>

      {/* Action Footer Button Group */}
      <div className="flex items-center gap-1.5 pt-2 border-t border-astro-border dark:border-astro-strong-border/80">
        <button
          onClick={() => onOpenCaseWorkspace(caseItem)}
          className="flex-1 min-h-[36px] px-2.5 py-1.5 rounded-lg bg-astro-secondary-surface0 hover:bg-astro-secondary text-white text-xs font-bold flex items-center justify-center gap-1 transition-all shadow-xs cursor-pointer"
        >
          <span>{isHi ? 'केस विवरण' : 'Case Workspace'}</span>
          <ChevronRight className="w-3.5 h-3.5" />
        </button>

        {onOpenProAnalysis && (
          <button
            onClick={() => onOpenProAnalysis(caseItem)}
            title={isHi ? 'विशारद विश्लेषण खोलें' : 'Open Pro Analysis'}
            className="p-2 rounded-lg bg-astro-secondary-surface dark:bg-astro-secondary hover:bg-astro-secondary-surface0/10 hover:text-amber-600 text-astro-secondary-text dark:text-astro-muted transition-colors cursor-pointer border border-astro-border dark:border-astro-strong-border"
          >
            <Compass className="w-4 h-4" />
          </button>
        )}

        {onOpenAcharyaConsultation && (
          <button
            onClick={() => onOpenAcharyaConsultation(caseItem)}
            title={isHi ? 'आचार्य परामर्श खोलें' : 'Open Acharya Consultation'}
            className="p-2 rounded-lg bg-astro-secondary-surface dark:bg-astro-secondary hover:bg-orange-500/10 hover:text-orange-600 text-astro-secondary-text dark:text-astro-muted transition-colors cursor-pointer border border-astro-border dark:border-astro-strong-border"
          >
            <Award className="w-4 h-4" />
          </button>
        )}

        {onPrepareReport && (
          <button
            onClick={() => onPrepareReport(caseItem)}
            title={isHi ? 'रिपोर्ट तैयार करें' : 'Prepare Report'}
            className="p-2 rounded-lg bg-astro-secondary-surface dark:bg-astro-secondary hover:bg-indigo-500/10 hover:text-indigo-600 text-astro-secondary-text dark:text-astro-muted transition-colors cursor-pointer border border-astro-border dark:border-astro-strong-border"
          >
            <FileText className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};
